import { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import * as XLSX from 'xlsx';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { ImportStatementDialog, ReviewTransactionsDialog } from "../components/contabilita";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, TableProperties, CalendarPlus, FileUp, Landmark, Tags, FileSpreadsheet } from "lucide-react";

import { VersamentoDialog } from "../components/prima-nota/VersamentoDialog";
import { NuovaRigaDialog } from "../components/prima-nota/NuovaRigaDialog";
import { EntrataDialog } from "../components/prima-nota/EntrataDialog";
import { UscitaDialog } from "../components/prima-nota/UscitaDialog";
import { EditGiornoDialog } from "../components/prima-nota/EditGiornoDialog";
import { RegistroGiornaliero } from "../components/prima-nota/RegistroGiornaliero";
import { RegistroUscite } from "../components/prima-nota/RegistroUscite";
import { TipologieUscitaDialog } from "../components/prima-nota/TipologieUscitaDialog";
import { ImportStoricoDialog } from "../components/prima-nota/ImportStoricoDialog";
import DateRangeFilter from "../components/DateRangeFilter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA_INIZIO_ATTIVITA = "2025-12-12";
const GIORNI_SETTIMANA = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

const formatDateIT = (dateStr, includeDay = false) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  const formatted = `${day}.${month}.${year.slice(2)}`;
  if (includeDay) {
    const date = new Date(dateStr);
    return `${GIORNI_SETTIMANA[date.getDay()]} ${formatted}`;
  }
  return formatted;
};

const PrimaNota = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const [serviziDelivery, setServiziDelivery] = useState([]);
  const [tipologieUscita, setTipologieUscita] = useState([]);
  const [sottotipologieUscita, setSottotipologieUscita] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [banchePos, setBanchePos] = useState([]);
  const activeBanchePos = useMemo(() => banchePos.filter((bank) => bank.attiva !== false), [banchePos]);
  const bancaNomeById = useMemo(() => new Map(banchePos.map((bank) => [bank.id, bank.nome])), [banchePos]);
  const [loading, setLoading] = useState(true);

  const [registroInizio, setRegistroInizio] = useState(DATA_INIZIO_ATTIVITA);
  const [fondoApertura, setFondoApertura] = useState(0);
  const [registroFine, setRegistroFine] = useState(new Date().toISOString().split('T')[0]);
  const [registroEntrate, setRegistroEntrate] = useState([]);
  const registroRequestRef = useRef(0);
  const [registroUscite, setRegistroUscite] = useState([]);
  const [versamentiBanca, setVersamentiBanca] = useState([]);
  const [dialogVersamento, setDialogVersamento] = useState(false);
  const [versamentoForm, setVersamentoForm] = useState({ data: new Date().toISOString().split('T')[0], importo: "", note: "" });
  const [loadingRegistro, setLoadingRegistro] = useState(false);
  const [registroTab, setRegistroTab] = useState("giornaliero");

  const [editUscita, setEditUscita] = useState(null);
  const [editUscitaForm, setEditUscitaForm] = useState({});

  const [editGiornoDialog, setEditGiornoDialog] = useState(false);
  const [editGiornoData, setEditGiornoData] = useState(null);
  const [editGiornoNuovaData, setEditGiornoNuovaData] = useState("");
  const [entrateDelGiorno, setEntrateDelGiorno] = useState([]);

  const [filtroTipologia, setFiltroTipologia] = useState("__all__");
  const [filtroSottotipologia, setFiltroSottotipologia] = useState("__all__");
  const [filtroFornitore, setFiltroFornitore] = useState("__all__");
  const [filtroMetodo, setFiltroMetodo] = useState("__all__");
  const [filtroBanca, setFiltroBanca] = useState("__all__");

  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const [dialogType, setDialogType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({});
  const [nuovaRigaForm, setNuovaRigaForm] = useState({});
  const [newFornitoreDialog, setNewFornitoreDialog] = useState(false);

  const [importStatementDialog, setImportStatementDialog] = useState(false);
  const [importStoricoDialog, setImportStoricoDialog] = useState(false);
  const [reviewTransactionsDialog, setReviewTransactionsDialog] = useState(false);
  const [estrattoContoData, setEstrattoContoData] = useState(null);
  const [tipologieDialog, setTipologieDialog] = useState(false);

  // ===== FETCH DATA =====

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [serviziRes, tipologieRes, fornitoriRes, sottotipologieRes] = await Promise.all([
        axios.get(`${API}/servizi-delivery`, { headers }),
        axios.get(`${API}/tipologie-uscita`, { headers }),
        axios.get(`${API}/fornitori`, { headers }),
        axios.get(`${API}/sottotipologie-uscita`, { headers })
      ]);
      // Deduplicazione per nome (un locale può avere lo stesso servizio di un altro locale)
      const seen = new Set();
      setServiziDelivery(serviziRes.data.filter(s => seen.has(s.nome) ? false : seen.add(s.nome)));
      setTipologieUscita(tipologieRes.data);
      setSottotipologieUscita(sottotipologieRes.data);
      setFornitori(fornitoriRes.data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistro = async () => {
    const requestId = ++registroRequestRef.current;
    if (!localeCorrente) return;
    setLoadingRegistro(true);
    try {
      const headers = getAuthHeaders();
      const localeParam = `locale_id=${localeCorrente.id}`;
      const [entrateRes, usciteRes, fornitoriRes, versamentiRes, aperturaRes] = await Promise.all([
        axios.get(`${API}/entrate?data_inizio=${registroInizio}&data_fine=${registroFine}&${localeParam}`, { headers }),
        axios.get(`${API}/uscite?data_inizio=${registroInizio}&data_fine=${registroFine}&${localeParam}`, { headers }),
        axios.get(`${API}/fornitori`, { headers }),
        axios.get(`${API}/versamenti-banca?locale_id=${localeCorrente.id}&data_da=${registroInizio}&data_a=${registroFine}`, { headers }),
        axios.get(`${API}/prima-nota/saldo-apertura?locale_id=${localeCorrente.id}&data=${registroInizio}`, { headers })
      ]);
      if (requestId !== registroRequestRef.current) return;
      setRegistroEntrate(entrateRes.data);
      setRegistroUscite(usciteRes.data);
      setFornitori(fornitoriRes.data);
      setVersamentiBanca(versamentiRes.data);
      setFondoApertura(aperturaRes.data.fondo_apertura);
    } catch {
      if (requestId === registroRequestRef.current) toast.error("Errore nel caricamento registro");
    } finally {
      if (requestId === registroRequestRef.current) setLoadingRegistro(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    if (!localeCorrente?.id) return;
    try {
      const saved = JSON.parse(localStorage.getItem(`prima-nota-periodo:${localeCorrente.id}`) || "null");
      if (saved?.inizio && saved?.fine && saved.inizio <= saved.fine) {
        setRegistroInizio(saved.inizio); setRegistroFine(saved.fine);
      }
    } catch { /* L'intervallo predefinito resta disponibile. */ }
  }, [localeCorrente?.id]);
  useEffect(() => { fetchRegistro(); }, [registroInizio, registroFine, localeCorrente]);
  const changeRegistroRange = (inizio, fine) => {
    setRegistroInizio(inizio); setRegistroFine(fine);
    if (localeCorrente?.id) {
      try { localStorage.setItem(`prima-nota-periodo:${localeCorrente.id}`, JSON.stringify({ inizio, fine })); } catch { /* Storage non disponibile. */ }
    }
  };
  const showImportedPeriod = ({ data_da, data_a }) => {
    const samePeriod = registroInizio === data_da && registroFine === data_a;
    changeRegistroRange(data_da, data_a);
    setRegistroTab("giornaliero");
    if (samePeriod) fetchRegistro();
    fetchData();
  };
  const loadBanchePos = async () => {
    if (!localeCorrente?.id) {
      setBanchePos([]);
      return [];
    }
    const response = await axios.get(`${API}/locali/${localeCorrente.id}/settings`, { headers: getAuthHeaders() });
    const banks = response.data?.banche_pos || [];
    setBanchePos(banks);
    return banks;
  };

  useEffect(() => {
    loadBanchePos()
      .catch(() => setBanchePos([]));
  }, [localeCorrente]);

  // ===== COMPUTED DATA =====

  const metodiEntrata = useMemo(() => {
    const deliveryMethods = [];
    serviziDelivery.filter(s => s.attivo).forEach(s => {
      deliveryMethods.push(s.nome);
      deliveryMethods.push(`${s.nome} CASH`);
    });
    return ["Contanti", "POS", ...deliveryMethods];
  }, [serviziDelivery]);

  // Risolve la % di commissione valida ALLA DATA dell'entrata (storico percentuali)
  const getCommissionePerc = useMemo(() => {
    const byCol = {};
    serviziDelivery.forEach(s => {
      byCol[s.nome] = s;
      byCol[`${s.nome} CASH`] = s;
    });
    return (service, data) => {
      const s = byCol[service];
      if (!s) return 0;
      // Confronta solo la parte YYYY-MM-DD (ignora eventuali orari nella data)
      const d = (data || "").slice(0, 10);
      const storico = s.storico_commissioni || [];
      if (!storico.length) return s.commissione_percentuale || 0;
      let chosen = null;
      for (const h of storico) {
        const hd = (h.dal || "").slice(0, 10);
        if (hd <= d && (!chosen || hd > (chosen.dal || "").slice(0, 10))) chosen = h;
      }
      if (chosen) return chosen.percentuale || 0;
      // Data precedente al primo storico → usa la percentuale più vecchia
      const earliest = storico.reduce((a, b) => ((a.dal || "").slice(0, 10) <= (b.dal || "").slice(0, 10) ? a : b));
      return earliest.percentuale || 0;
    };
  }, [serviziDelivery]);

  const getTipologiaNome = (id) => tipologieUscita.find(t => t.id === id)?.nome || "-";
  const getSottotipologiaNome = (id) => sottotipologieUscita.find(t => t.id === id)?.nome || "-";
  const getFornitoreNome = (id) => fornitori.find(f => f.id === id)?.ragione_sociale || "-";

  const deliveryColumns = useMemo(() => {
    const columns = [];
    serviziDelivery.filter(s => s.attivo).forEach(s => {
      columns.push(s.nome);
      columns.push(`${s.nome} CASH`);
    });
    return columns;
  }, [serviziDelivery]);

  const registroPerGiorno = useMemo(() => {
    const dates = new Set([
      ...registroEntrate.map(e => e.data),
      ...registroUscite.map(u => u.data),
      ...versamentiBanca.map(v => v.data)
    ]);
    const sortedDates = Array.from(dates).sort();
    let totaleNettoAccumulato = 0;
    let fondoCassaAccumulato = fondoApertura;

    // Calcola in ordine cronologico (per fondo cassa progressivo), poi inverti per visualizzazione
    const rows = sortedDates.map((data, index) => {
      const entrateGiorno = registroEntrate.filter(e => e.data === data);
      const usciteGiorno = registroUscite.filter(u => u.data === data);
      const versamentiGiorno = versamentiBanca.filter(v => v.data === data);

      const contanti = entrateGiorno.filter(e => (e.metodo || "").toLowerCase() === "contanti").reduce((s, e) => s + e.importo, 0);
      const pos = entrateGiorno.filter(e => (e.metodo || "").toLowerCase() === "pos").reduce((s, e) => s + e.importo, 0);
      const posBreakdown = {};
      let hasDetailedPos = false;
      entrateGiorno.filter(e => (e.metodo || "").toLowerCase() === "pos").forEach((entry) => {
        if (Array.isArray(entry.split_pos) && entry.split_pos.length > 0) {
          hasDetailedPos = true;
          entry.split_pos.forEach((split) => {
            const nome = bancaNomeById.get(split.banca_id) || split.banca_nome || "POS";
            posBreakdown[nome] = (posBreakdown[nome] || 0) + (Number(split.importo) || 0);
          });
        } else {
          posBreakdown.Generico = (posBreakdown.Generico || 0) + (Number(entry.importo) || 0);
        }
      });

      const deliveryTotals = {};
      let deliveryCashTotal = 0;
      let commissioniTotali = 0;
      deliveryColumns.forEach(service => {
        const total = entrateGiorno.filter(e => e.metodo === service).reduce((s, e) => s + e.importo, 0);
        deliveryTotals[service] = total;
        const commissionePerc = getCommissionePerc(service, data);
        commissioniTotali += total * (commissionePerc / 100) * 1.22;
        if (service.toUpperCase().includes("CASH")) deliveryCashTotal += total;
      });

      const entrateLorde = entrateGiorno.reduce((s, e) => s + e.importo, 0);
      const entrateNette = entrateLorde - commissioniTotali;
      const usciteContanti = usciteGiorno.filter(u => u.metodo === "Contante" || u.metodo === "Contanti").reduce((s, u) => s + u.importo, 0);
      const usciteTotali = usciteGiorno.reduce((s, u) => s + u.importo, 0);
      const totaleVersamenti = versamentiGiorno.reduce((s, v) => s + v.importo, 0);

      const fondoCassaIniziale = index === 0 ? fondoApertura : fondoCassaAccumulato;
      const fondoCassaFinale = fondoCassaIniziale + contanti + deliveryCashTotal - usciteContanti - totaleVersamenti;
      fondoCassaAccumulato = fondoCassaFinale;
      const totaleNettoGiorno = entrateNette - usciteTotali;
      totaleNettoAccumulato += totaleNettoGiorno;

      return { data, contanti, pos, posBreakdown, hasDetailedPos, deliveryTotals, entrateLorde, entrateNette, commissioniTotali, usciteContanti, usciteTotali, versamentiBanca: totaleVersamenti, fondoCassaIniziale, fondoCassaFinale, totaleNetto: totaleNettoAccumulato };
    });
    return rows.reverse();
  }, [registroEntrate, registroUscite, versamentiBanca, deliveryColumns, getCommissionePerc, bancaNomeById, fondoApertura]);

  const usciteFiltrate = useMemo(() => {
    let filtered = [...registroUscite];
    if (filtroTipologia !== "__all__") filtered = filtered.filter(u => u.tipologia_id === filtroTipologia);
    if (filtroSottotipologia === "__none__") filtered = filtered.filter(u => !u.sottotipologia_id);
    else if (filtroSottotipologia !== "__all__") filtered = filtered.filter(u => u.sottotipologia_id === filtroSottotipologia);
    if (filtroFornitore !== "__all__") filtered = filtered.filter(u => u.fornitore_id === filtroFornitore);
    if (filtroMetodo !== "__all__") filtered = filtered.filter(u => u.metodo === filtroMetodo);
    if (filtroBanca !== "__all__") {
      filtered = filtered.filter((u) => filtroBanca === "__generic__" ? !u.banca_id : u.banca_id === filtroBanca);
    }
    return filtered.sort((a, b) => b.data.localeCompare(a.data));
  }, [registroUscite, filtroTipologia, filtroSottotipologia, filtroFornitore, filtroMetodo, filtroBanca]);

  const bancheUscite = useMemo(() => {
    const byId = new Map(banchePos.map((bank) => [bank.id, bank.nome]));
    registroUscite.forEach((uscita) => {
      if (uscita.banca_id && !byId.has(uscita.banca_id)) byId.set(uscita.banca_id, uscita.banca_nome || "Banca storica");
    });
    return Array.from(byId, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [banchePos, registroUscite]);

  const totaleUsciteFiltrate = useMemo(() => usciteFiltrate.reduce((s, u) => s + u.importo, 0), [usciteFiltrate]);

  // ===== HANDLERS =====

  const openNuovaRiga = () => {
    const today = new Date().toISOString().split('T')[0];
    const initialForm = { data: today };
    metodiEntrata.forEach(m => { initialForm[m] = ""; });
    initialForm.split_pos = Object.fromEntries(activeBanchePos.map((bank) => [bank.id, ""]));
    setNuovaRigaForm(initialForm);
    setDialogType('nuovaRiga');
  };

  const openImportStatement = async () => {
    setDialogType(null);
    setReviewTransactionsDialog(false);
    setEstrattoContoData(null);
    try {
      await loadBanchePos();
      setImportStatementDialog(true);
    } catch {
      toast.error("Impossibile caricare le banche del locale");
    }
  };

  const handleSubmitNuovaRiga = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    const { data, ...values } = nuovaRigaForm;
    if (!data) { toast.error("Seleziona una data"); return; }
    try {
      let count = 0;
      for (const metodo of metodiEntrata) {
        if (metodo === "POS" && activeBanchePos.length > 0) {
          const split_pos = activeBanchePos.map((bank) => ({
            banca_id: bank.id,
            importo: Number(values.split_pos?.[bank.id]) || 0,
          }));
          const importo = split_pos.reduce((sum, item) => sum + item.importo, 0);
          if (importo > 0) {
            await axios.post(`${API}/entrate`, { data, importo, metodo: "POS", split_pos, descrizione: "", locale_id: localeCorrente?.id }, { headers });
            count++;
          }
          continue;
        }
        const importo = parseFloat(values[metodo]);
        if (importo && importo > 0) {
          await axios.post(`${API}/entrate`, { data, importo, metodo, descrizione: "", locale_id: localeCorrente?.id }, { headers });
          count++;
        }
      }
      if (count === 0) { toast.error("Inserisci almeno un valore"); return; }
      toast.success(`${count} entrate create per ${formatDateIT(data)}`);
      setDialogType(null);
      fetchRegistro();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const handleSubmitEntrata = async (e) => {
    e.preventDefault();
    if (!form.metodo) { toast.error("Seleziona il metodo"); return; }
    try {
      const headers = getAuthHeaders();
      const isDetailedPos = form.metodo.toLowerCase() === "pos" && activeBanchePos.length > 0;
      const split_pos = isDetailedPos ? activeBanchePos.map((bank) => ({
        banca_id: bank.id,
        importo: Number(form.split_pos?.[bank.id]) || 0,
      })) : undefined;
      const importo = isDetailedPos
        ? split_pos.reduce((sum, item) => sum + item.importo, 0)
        : parseFloat(form.importo);
      if (!importo || importo <= 0) { toast.error("Inserisci un importo valido"); return; }
      await axios.post(`${API}/entrate`, { ...form, importo, split_pos, locale_id: localeCorrente?.id }, { headers });
      toast.success("Entrata registrata");
      setDialogType(null);
      fetchRegistro();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const handleSubmitUscita = async (e) => {
    e.preventDefault();
    if (!form.importo || !form.metodo || !form.tipologia_id) { toast.error("Compila tutti i campi obbligatori"); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/uscite`, { ...form, importo: parseFloat(form.importo), fornitore_id: form.fornitore_id || null, locale_id: localeCorrente?.id }, { headers });
      toast.success("Uscita registrata");
      setDialogType(null);
      fetchRegistro();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const handleSaveVersamento = async (e) => {
    e.preventDefault();
    if (!versamentoForm.importo || parseFloat(versamentoForm.importo) <= 0) { toast.error("Inserisci un importo valido"); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/versamenti-banca`, { locale_id: localeCorrente?.id, data: versamentoForm.data, importo: parseFloat(versamentoForm.importo), note: versamentoForm.note || "" }, { headers });
      toast.success("Versamento registrato");
      setDialogVersamento(false);
      setVersamentoForm({ data: new Date().toISOString().split('T')[0], importo: "", note: "" });
      fetchRegistro();
    } catch { toast.error("Errore nel salvataggio del versamento"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/${deleteTarget.type}/${deleteTarget.id}`, { headers });
      toast.success("Eliminato");
      setDeleteTarget(null);
      fetchRegistro();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  // Cell editing
  const startEditCell = (data, metodo, currentValue) => { setEditingCell({ data, metodo }); setEditingValue(currentValue.toString()); };
  const cancelEditCell = () => { setEditingCell(null); setEditingValue(""); };
  const saveEditCell = async () => {
    if (!editingCell) return;
    const { data, metodo } = editingCell;
    const newValue = parseFloat(editingValue) || 0;
    const headers = getAuthHeaders();
    const existingEntries = registroEntrate.filter(e => e.data === data && e.metodo === metodo);
    try {
      if (newValue === 0 && existingEntries.length > 0) {
        for (const entry of existingEntries) await axios.delete(`${API}/entrate/${entry.id}`, { headers });
        toast.success("Entrata eliminata");
      } else if (existingEntries.length === 0 && newValue > 0) {
        await axios.post(`${API}/entrate`, { data, importo: newValue, metodo, descrizione: "", locale_id: localeCorrente?.id }, { headers });
        toast.success("Entrata creata");
      } else if (existingEntries.length === 1) {
        await axios.put(`${API}/entrate/${existingEntries[0].id}`, { data, importo: newValue, metodo, descrizione: existingEntries[0].descrizione || "" }, { headers });
        toast.success("Entrata aggiornata");
      } else if (existingEntries.length > 1) {
        for (const entry of existingEntries) await axios.delete(`${API}/entrate/${entry.id}`, { headers });
        await axios.post(`${API}/entrate`, { data, importo: newValue, metodo, descrizione: "", locale_id: localeCorrente?.id }, { headers });
        toast.success("Entrate consolidate");
      }
      cancelEditCell();
      fetchRegistro();
    } catch (err) {
      console.error("Errore salvataggio cella:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  // Uscite edit
  const startEditUscita = (item) => { setEditUscita(item); setEditUscitaForm({ data: item.data, importo: item.importo.toString(), metodo: item.metodo, tipologia_id: item.tipologia_id, sottotipologia_id: item.sottotipologia_id || null, fornitore_id: item.fornitore_id || "", descrizione: item.descrizione || "" }); };
  const cancelEditUscita = () => { setEditUscita(null); setEditUscitaForm({}); };
  const saveEditUscita = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/uscite/${editUscita.id}`, { ...editUscitaForm, importo: parseFloat(editUscitaForm.importo), fornitore_id: editUscitaForm.fornitore_id || null }, { headers });
      toast.success("Uscita aggiornata");
      cancelEditUscita();
      fetchRegistro();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  // Edit giorno
  const openEditGiornoDialog = (data) => {
    setEditGiornoData(data);
    setEditGiornoNuovaData(data);
    setEntrateDelGiorno(registroEntrate.filter(e => e.data === data));
    setEditGiornoDialog(true);
  };

  const saveEntrateGiorno = async () => {
    if (editGiornoNuovaData === editGiornoData) { setEditGiornoDialog(false); return; }
    try {
      const headers = getAuthHeaders();
      for (const entrata of entrateDelGiorno) {
        await axios.put(`${API}/entrate/${entrata.id}`, { data: editGiornoNuovaData, importo: entrata.importo, metodo: entrata.metodo, descrizione: entrata.descrizione || "" }, { headers });
      }
      toast.success(`${entrateDelGiorno.length} entrate spostate al ${formatDateIT(editGiornoNuovaData, true)}`);
      setEditGiornoDialog(false);
      fetchRegistro();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const deleteEntrateGiorno = async () => {
    try {
      const headers = getAuthHeaders();
      let errors = 0;
      // Elimina entrate del giorno
      for (const entrata of entrateDelGiorno) {
        try { await axios.delete(`${API}/entrate/${entrata.id}`, { headers }); } catch { errors++; }
      }
      // Elimina anche uscite del giorno
      const usciteGiorno = registroUscite.filter(u => u.data === editGiornoData);
      for (const uscita of usciteGiorno) {
        try { await axios.delete(`${API}/uscite/${uscita.id}`, { headers }); } catch { errors++; }
      }
      // Elimina versamenti del giorno
      const versamentiGiorno = versamentiBanca.filter(v => v.data === editGiornoData);
      for (const v of versamentiGiorno) {
        try { await axios.delete(`${API}/versamenti-banca/${v.id}`, { headers }); } catch { errors++; }
      }
      const totale = entrateDelGiorno.length + usciteGiorno.length + versamentiGiorno.length;
      if (errors > 0) {
        toast.warning(`Eliminati ${totale - errors}/${totale} elementi (${errors} errori)`);
      } else {
        toast.success(`${totale} elementi eliminati`);
      }
      setEditGiornoDialog(false);
      fetchRegistro();
    } catch (error) { toast.error(`Errore nell'eliminazione: ${error.response?.data?.detail || error.message}`); }
  };

  // Export
  const exportExcel = () => {
    const posBankNames = Array.from(new Set(registroPerGiorno.flatMap((row) => Object.keys(row.posBreakdown || {}))));
    const entrateData = registroPerGiorno.map(row => {
      const rowData = { 'Data': formatDateIT(row.data), 'Contanti': row.contanti, 'POS': row.pos };
      posBankNames.forEach((nome) => { rowData[`POS - ${nome}`] = row.posBreakdown?.[nome] || 0; });
      deliveryColumns.forEach(col => { rowData[col] = row.deliveryTotals[col] || 0; });
      rowData['Entrate Lorde'] = row.entrateLorde; rowData['Entrate Nette'] = row.entrateNette;
      rowData['Commissioni'] = row.commissioniTotali; rowData['Uscite Contanti'] = row.usciteContanti;
      rowData['Uscite Totali'] = row.usciteTotali; rowData['Versamenti Banca'] = row.versamentiBanca;
      rowData['Fondo Cassa Iniz.'] = row.fondoCassaIniziale; rowData['Fondo Cassa Fin.'] = row.fondoCassaFinale;
      rowData['Totale Netto'] = row.totaleNetto;
      return rowData;
    });
    const usciteData = usciteFiltrate.map(u => ({ 'Data': formatDateIT(u.data), 'Importo': u.importo, 'Metodo': u.metodo, 'Banca': bancaNomeById.get(u.banca_id) || u.banca_nome || 'Generico', 'Tipologia': getTipologiaNome(u.tipologia_id), 'Sotto-tipologia': u.sottotipologia_id ? getSottotipologiaNome(u.sottotipologia_id) : '', 'Fornitore': getFornitoreNome(u.fornitore_id), 'Descrizione': u.descrizione || '' }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(entrateData), "Registro Entrate");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(usciteData), "Registro Uscite");
    XLSX.writeFile(wb, `Prima_Nota_${formatDateIT(registroInizio)}_${formatDateIT(registroFine)}.xlsx`);
    toast.success("Excel esportato");
  };

  const exportUsciteExcel = () => {
    const data = usciteFiltrate.map(u => ({ 'Data': formatDateIT(u.data), 'Importo': u.importo, 'Metodo': u.metodo, 'Banca': bancaNomeById.get(u.banca_id) || u.banca_nome || 'Generico', 'Tipologia': getTipologiaNome(u.tipologia_id), 'Sotto-tipologia': u.sottotipologia_id ? getSottotipologiaNome(u.sottotipologia_id) : '', 'Fornitore': getFornitoreNome(u.fornitore_id), 'Descrizione': u.descrizione || '' }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Uscite");
    XLSX.writeFile(wb, `Uscite_${formatDateIT(registroInizio)}_${formatDateIT(registroFine)}.xlsx`);
    toast.success("Excel esportato");
  };

  // ===== RENDER =====

  return (
    <div className="page-container" data-testid="prima-nota-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Cassa & Contabilità</h1>
          <p className="page-subtitle">Registro entrate e uscite</p>
        </div>
      </div>

      <div className="bg-card backdrop-blur-sm border border-border rounded-2xl p-4">
        <DateRangeFilter
          dataInizio={registroInizio}
          dataFine={registroFine}
          onRangeChange={changeRegistroRange}
          className="mb-3"
          data-testid="primanota-date-filter"
        />
        <div className="flex flex-wrap gap-2">
            <Button onClick={() => setImportStoricoDialog(true)} variant="outline" size="sm" className="gap-1 border-border" data-testid="import-storico-button" disabled={!localeCorrente?.id}>
              <FileSpreadsheet className="h-4 w-4" /> Importa storico
            </Button>
            <Button onClick={openImportStatement} variant="outline" size="sm" className="gap-1 border-border text-foreground hover:bg-accent hover:text-accent-foreground" data-testid="import-statement-btn">
              <FileUp className="h-4 w-4" />
              <span className="hidden sm:inline">Importa EC</span>
              <span className="sm:hidden">EC</span>
            </Button>
            <Button onClick={openNuovaRiga} variant="outline" size="sm" className="gap-1 border-border text-foreground hover:bg-accent hover:text-accent-foreground" data-testid="new-daily-row-button">
              <CalendarPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuova Riga</span>
              <span className="sm:hidden">Riga</span>
            </Button>
            <Button onClick={() => { setForm({ data: new Date().toISOString().split('T')[0], importo: "", metodo: "", split_pos: Object.fromEntries(activeBanchePos.map((bank) => [bank.id, ""])) }); setDialogType('entrata'); }} size="sm" className="gap-1 bg-lime-500 hover:bg-lime-400 text-black" data-testid="new-entry-button">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Nuova Entrata</span>
              <span className="sm:hidden">Entrata</span>
            </Button>
            <Button onClick={() => { setForm({ data: new Date().toISOString().split('T')[0], importo: "", metodo: "", tipologia_id: "", sottotipologia_id: null, fornitore_id: "", descrizione: "" }); setDialogType('uscita'); }} variant="destructive" size="sm" className="gap-1" data-testid="new-expense-button">
              <TrendingDown className="h-4 w-4" />
              <span className="hidden sm:inline">Nuova Uscita</span>
              <span className="sm:hidden">Uscita</span>
            </Button>
            <Button onClick={() => setDialogVersamento(true)} size="sm" className="gap-1" style={{ backgroundColor: '#b45309', color: '#fff' }}>
              <Landmark className="h-4 w-4" />
              <span className="hidden sm:inline">Versamento</span>
              <span className="sm:hidden">Vers.</span>
            </Button>
            <Button onClick={() => setTipologieDialog(true)} variant="outline" size="sm" className="gap-1 border-border text-foreground hover:bg-accent hover:text-accent-foreground" data-testid="gestisci-tipologie-btn">
              <Tags className="h-4 w-4" />
              <span className="hidden sm:inline">Tipologie</span>
              <span className="sm:hidden">Tip.</span>
            </Button>
          </div>
      </div>

      <Tabs value={registroTab} onValueChange={setRegistroTab} className={`w-full ${importStoricoDialog ? "hidden" : ""}`}>
        <TabsList className="tabs-pill-list">
          <TabsTrigger value="giornaliero" className="flex items-center gap-2">
            <TableProperties className="h-4 w-4" />
            <span className="hidden sm:inline">Registro Giornaliero</span>
            <span className="sm:hidden">Registro</span>
          </TabsTrigger>
          <TabsTrigger value="uscite" className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            <span className="hidden sm:inline">Registro Uscite ({registroUscite.length})</span>
            <span className="sm:hidden">Uscite ({registroUscite.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="giornaliero" className="mt-4">
          <RegistroGiornaliero
            registroPerGiorno={registroPerGiorno}
            deliveryColumns={deliveryColumns}
            loadingRegistro={loadingRegistro}
            editingCell={editingCell}
            editingValue={editingValue}
            onEditingValueChange={setEditingValue}
            onStartEditCell={startEditCell}
            onSaveEditCell={saveEditCell}
            onCancelEditCell={cancelEditCell}
            onEditGiorno={openEditGiornoDialog}
            onExport={exportExcel}
            formatDateIT={formatDateIT}
          />
        </TabsContent>

        <TabsContent value="uscite" className="mt-4">
          <RegistroUscite
            usciteFiltrate={usciteFiltrate}
            totaleUsciteFiltrate={totaleUsciteFiltrate}
            registroUsciteCount={registroUscite.length}
            tipologieUscita={tipologieUscita}
            sottotipologieUscita={sottotipologieUscita}
            fornitori={fornitori}
            filtroTipologia={filtroTipologia}
            filtroSottotipologia={filtroSottotipologia}
            filtroFornitore={filtroFornitore}
            filtroMetodo={filtroMetodo}
            filtroBanca={filtroBanca}
            banche={bancheUscite}
            onFiltroTipologiaChange={(id) => { setFiltroTipologia(id); setFiltroSottotipologia("__all__"); }}
            onFiltroSottotipologiaChange={setFiltroSottotipologia}
            onFiltroFornitoreChange={setFiltroFornitore}
            onFiltroMetodoChange={setFiltroMetodo}
            onFiltroBancaChange={setFiltroBanca}
            getBancaNome={(item) => bancaNomeById.get(item.banca_id) || item.banca_nome || "Generico"}
            editUscita={editUscita}
            editUscitaForm={editUscitaForm}
            onStartEditUscita={startEditUscita}
            onSaveEditUscita={saveEditUscita}
            onCancelEditUscita={cancelEditUscita}
            onEditUscitaFormChange={setEditUscitaForm}
            onDeleteTarget={setDeleteTarget}
            onNewFornitore={() => setNewFornitoreDialog(true)}
            onExport={exportUsciteExcel}
            loadingRegistro={loadingRegistro}
            formatDateIT={formatDateIT}
            getTipologiaNome={getTipologiaNome}
            getSottotipologiaNome={getSottotipologiaNome}
            getFornitoreNome={getFornitoreNome}
          />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {dialogType === 'nuovaRiga' && <NuovaRigaDialog open onOpenChange={(nextOpen) => !nextOpen && setDialogType(null)} form={nuovaRigaForm} onFormChange={setNuovaRigaForm} metodiEntrata={metodiEntrata} banchePos={activeBanchePos} onSubmit={handleSubmitNuovaRiga} />}
      {dialogType === 'entrata' && <EntrataDialog open onOpenChange={(nextOpen) => !nextOpen && setDialogType(null)} form={form} onFormChange={setForm} metodiEntrata={metodiEntrata} banchePos={activeBanchePos} onSubmit={handleSubmitEntrata} />}
      {dialogType === 'uscita' && <UscitaDialog open onOpenChange={(nextOpen) => !nextOpen && setDialogType(null)} form={form} onFormChange={setForm} tipologieUscita={tipologieUscita} sottotipologieUscita={sottotipologieUscita} fornitori={fornitori} getAuthHeaders={getAuthHeaders} onFornitoreCreated={(f) => setFornitori(prev => [...prev, f])} onSubmit={handleSubmitUscita} />}
      <VersamentoDialog open={dialogVersamento} onOpenChange={setDialogVersamento} form={versamentoForm} onFormChange={setVersamentoForm} onSubmit={handleSaveVersamento} />
      <EditGiornoDialog open={editGiornoDialog} onOpenChange={setEditGiornoDialog} data={editGiornoData} nuovaData={editGiornoNuovaData} onNuovaDataChange={setEditGiornoNuovaData} entrateDelGiorno={entrateDelGiorno} bancaNomeById={bancaNomeById} onSave={saveEntrateGiorno} onDelete={deleteEntrateGiorno} formatDateIT={formatDateIT} />
      <DeleteConfirmDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)} onConfirm={handleDelete} title="Conferma eliminazione" description="Sei sicuro di voler eliminare questo elemento?" />
      {importStatementDialog && <ImportStatementDialog open onOpenChange={setImportStatementDialog} getAuthHeaders={getAuthHeaders} localeId={localeCorrente?.id} banchePos={activeBanchePos} onSuccess={(data) => { setEstrattoContoData(data); setReviewTransactionsDialog(true); }} />}
      {importStoricoDialog && <ImportStoricoDialog open onOpenChange={setImportStoricoDialog} localeId={localeCorrente?.id} localeNome={localeCorrente?.nome} getAuthHeaders={getAuthHeaders} tipologie={tipologieUscita} sottotipologie={sottotipologieUscita} fornitori={fornitori} onTypeCreated={t => setTipologieUscita(items => [...items, t])} onSubtypeCreated={s => setSottotipologieUscita(items => [...items, s])} onSupplierCreated={s => setFornitori(items => [...items, s])} onComplete={showImportedPeriod} />}
      {reviewTransactionsDialog && <ReviewTransactionsDialog open onOpenChange={setReviewTransactionsDialog} data={estrattoContoData} fornitori={fornitori} tipologie={tipologieUscita} getAuthHeaders={getAuthHeaders} localeId={localeCorrente?.id} onImportComplete={() => { setEstrattoContoData(null); fetchRegistro(); }} onFornitoreCreated={(f) => setFornitori(prev => [...prev, f])} />}
      <TipologieUscitaDialog open={tipologieDialog} onOpenChange={setTipologieDialog} tipologie={tipologieUscita} sottotipologie={sottotipologieUscita} getAuthHeaders={getAuthHeaders} onChanged={fetchData} />
    </div>
  );
};

export default PrimaNota;
